import React, { useState, useEffect } from 'react';
import { ref, get, update, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { db } from './firebase';

const COMMISSION_RATE = 0.30;
const inp = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid #E5E7EB', fontSize: 13,
  backgroundColor: '#F7F8FA', outline: 'none', boxSizing: 'border-box',
};

export default function PrescriptionTab({ pharmacyId, pharmacyData }) {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [quoteModal, setQuoteModal] = useState(null);
  const [quoteItems, setQuoteItems] = useState([]);
  const [quoteDeliveryFee, setQuoteDeliveryFee] = useState('12');
  const [submittingQuote, setSubmittingQuote] = useState(false);

  useEffect(() => {
    // Two indexed, scoped queries instead of the entire global `prescriptions`
    // node: at scale that meant every pharmacy's dashboard downloaded every
    // OTHER pharmacy's prescription records too — including their embedded
    // base64 photos. `prescriptions/{id}/imageData` is a data: URI, often
    // several hundred KB to a few MB each, so a full-node listener here was
    // by far the single most expensive read pattern in the whole system.
    // An unclaimed prescription is always status 'pending' (quoting sets
    // pharmacyId AND moves it to 'quoted' in the same update — see App.js's
    // own pendingPrescriptions counter, which relies on the same fact) — a
    // more reliable signal than pharmacyId itself, since older records have
    // that field entirely absent rather than an empty string.
    let pending = [];
    let mine = [];
    const publish = () => {
      const merged = [...pending, ...mine.filter(m => !pending.some(u => u.id === m.id))]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setPrescriptions(merged);
      setLoading(false);
    };
    const unsub1 = onValue(query(ref(db, 'prescriptions'), orderByChild('status'), equalTo('pending')), snap => {
      pending = snap.exists() ? Object.entries(snap.val()).map(([k, v]) => ({ id: k, ...v })) : [];
      publish();
    });
    const unsub2 = onValue(query(ref(db, 'prescriptions'), orderByChild('pharmacyId'), equalTo(pharmacyId)), snap => {
      mine = snap.exists() ? Object.entries(snap.val()).map(([k, v]) => ({ id: k, ...v })) : [];
      publish();
    });
    return () => { unsub1(); unsub2(); };
  }, [pharmacyId]);

  const updateStatus = async (id, status, rejectionReason) => {
    await update(ref(db, `prescriptions/${id}`), {
      status,
      ...(rejectionReason ? { rejectionReason } : {}),
      reviewedAt: new Date().toISOString(),
      reviewedBy: pharmacyData?.pharmacyName,
    });
  };

  // ── QUOTE BUILDER ─────────────────────────────────────────────────────────
  // Replaces the old plain "Approve" — approving a prescription only means
  // anything once the customer can actually see a price and pay for it.
  const openQuoteModal = (p) => {
    const medicines = p.aiMedicines || p.analysisResult?.medicines || [];
    setQuoteItems(
      medicines.length > 0
        ? medicines.map(m => ({ name: m.name || '', quantity: '1', pharmacyPrice: '' }))
        : [{ name: '', quantity: '1', pharmacyPrice: '' }]
    );
    setQuoteDeliveryFee('10');
    setQuoteModal(p);
  };

  const updateQuoteItem = (i, field, value) => {
    setQuoteItems(items => items.map((it, idx) => idx === i ? { ...it, [field]: value } : it));
  };
  const addQuoteRow = () => setQuoteItems(items => [...items, { name: '', quantity: '1', pharmacyPrice: '' }]);
  const removeQuoteRow = (i) => setQuoteItems(items => items.filter((_, idx) => idx !== i));

  const quoteCustomerSubtotal = quoteItems.reduce((s, it) => {
    const price = parseFloat(it.pharmacyPrice) || 0;
    const qty = parseInt(it.quantity) || 0;
    return s + price * (1 + COMMISSION_RATE) * qty;
  }, 0);
  const quotePharmacySubtotal = quoteItems.reduce((s, it) => s + (parseFloat(it.pharmacyPrice) || 0) * (parseInt(it.quantity) || 0), 0);
  const quoteDeliveryFeeNum = parseFloat(quoteDeliveryFee) || 0;
  const quoteTotal = quoteCustomerSubtotal + quoteDeliveryFeeNum;

  const submitQuote = async () => {
    const validItems = quoteItems.filter(it => it.name.trim() && parseInt(it.quantity) > 0 && parseFloat(it.pharmacyPrice) > 0);
    if (validItems.length === 0) {
      alert('Add at least one medicine with a name, quantity and price.');
      return;
    }
    setSubmittingQuote(true);
    try {
      // Guard against two pharmacies quoting the same prescription at once —
      // prescriptions with no pharmacyId are visible to every pharmacy.
      const freshSnap = await get(ref(db, `prescriptions/${quoteModal.id}`));
      if (freshSnap.val()?.status !== 'pending') {
        alert('This prescription was already handled — refreshing your list.');
        setQuoteModal(null);
        setSubmittingQuote(false);
        return;
      }

      const items = validItems.map((it, i) => {
        const pharmacyPrice = parseFloat(it.pharmacyPrice);
        const customerPrice = parseFloat((pharmacyPrice * (1 + COMMISSION_RATE)).toFixed(2));
        return {
          id: `rx_${i}`,
          name: it.name.trim(),
          quantity: parseInt(it.quantity),
          pharmacyPrice, customerPrice, price: customerPrice,
          pharmacyId, pharmacy: pharmacyData?.pharmacyName || '',
          requiresPrescription: true,
        };
      });

      await update(ref(db, `prescriptions/${quoteModal.id}`), {
        status: 'quoted',
        pharmacyId,
        pharmacyName: pharmacyData?.pharmacyName || '',
        quote: {
          items,
          deliveryFee: quoteDeliveryFeeNum,
          customerSubtotal: parseFloat(quoteCustomerSubtotal.toFixed(2)),
          pharmacySubtotal: parseFloat(quotePharmacySubtotal.toFixed(2)),
          total: parseFloat(quoteTotal.toFixed(2)),
        },
        quotedAt: new Date().toISOString(),
        quotedBy: pharmacyData?.pharmacyName || '',
      });

      setQuoteModal(null);
      alert('✅ Quote sent! The customer will be notified to review and pay.');
    } catch (e) {
      console.error('submitQuote failed', e);
      alert('Could not send quote: ' + (e.message || 'Please try again.'));
    } finally { setSubmittingQuote(false); }
  };

  const filtered = filter === 'all' ? prescriptions : prescriptions.filter(p => p.status === filter);
  const pendingCount = prescriptions.filter(p => p.status === 'pending').length;

  const STATUS_META = {
    pending:  { border: '#FCD34D', headerBg: '#FFFBEB', badgeBg: '#FEF3C7', badgeColor: '#92400E', label: '⏳ Pending Review' },
    quoted:   { border: '#93C5FD', headerBg: '#EFF6FF', badgeBg: '#DBEAFE', badgeColor: '#1D4ED8', label: '💰 Quoted — awaiting payment' },
    ordered:  { border: '#86EFAC', headerBg: '#F0FDF4', badgeBg: '#DCFCE7', badgeColor: '#15803D', label: '✅ Ordered & Paid' },
    rejected: { border: '#FCA5A5', headerBg: '#FFF5F5', badgeBg: '#FEE2E2', badgeColor: '#B91C1C', label: '✕ Rejected' },
  };
  const statusMeta = (status) => STATUS_META[status] || STATUS_META.pending;

  const CONF_STYLE = {
    high:   { bg: '#DCFCE7', color: '#15803D' },
    High:   { bg: '#DCFCE7', color: '#15803D' },
    medium: { bg: '#FEF3C7', color: '#92400E' },
    Medium: { bg: '#FEF3C7', color: '#92400E' },
    low:    { bg: '#FEE2E2', color: '#B91C1C' },
    Low:    { bg: '#FEE2E2', color: '#B91C1C' },
  };

  return (
    <div>
      {/* QUOTE BUILDER MODAL */}
      {quoteModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 28, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontWeight: 700, margin: 0, fontSize: 17 }}>💰 Build Quote for {quoteModal.customerName}</h3>
              <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }} onClick={() => setQuoteModal(null)}>✕</button>
            </div>
            <p style={{ fontSize: 13, color: '#888', margin: '0 0 16px' }}>
              Confirm what you have in stock and set your price for each medicine. The customer will be notified to review and pay — PharmDev adds 30% commission on top of your price, same as regular orders.
            </p>

            {quoteItems.map((it, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input style={inp} placeholder="Medicine name" value={it.name} onChange={e => updateQuoteItem(i, 'name', e.target.value)} />
                <input style={inp} type="number" placeholder="Qty" min="1" value={it.quantity} onChange={e => updateQuoteItem(i, 'quantity', e.target.value)} />
                <input style={inp} type="number" placeholder="Your price" min="0" step="0.01" value={it.pharmacyPrice} onChange={e => updateQuoteItem(i, 'pharmacyPrice', e.target.value)} />
                <button style={{ background: 'none', border: 'none', color: '#B91C1C', cursor: 'pointer', fontSize: 16 }} onClick={() => removeQuoteRow(i)}>✕</button>
              </div>
            ))}
            <button style={{ padding: '7px 14px', backgroundColor: '#F1F3F5', color: '#555', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, marginBottom: 16 }} onClick={addQuoteRow}>
              + Add medicine
            </button>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Delivery Fee (GH₵)</label>
              <input style={{ ...inp, width: 140 }} type="number" min="0" step="0.5" value={quoteDeliveryFee} onChange={e => setQuoteDeliveryFee(e.target.value)} />
            </div>

            <div style={{ backgroundColor: '#F0FDF4', borderRadius: 10, padding: 14, marginBottom: 20, border: '1px solid #BBF7D0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, color: '#555' }}>Medicines (customer pays)</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>GH₵ {quoteCustomerSubtotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, color: '#555' }}>Delivery fee</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>GH₵ {quoteDeliveryFeeNum.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #BBF7D0', paddingTop: 8, marginTop: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>Customer will pay</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#15803D' }}>GH₵ {quoteTotal.toFixed(2)}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ flex: 1, padding: '12px', border: '1px solid #E5E7EB', borderRadius: 10, cursor: 'pointer', backgroundColor: '#F7F8FA', color: '#555', fontSize: 14 }} onClick={() => setQuoteModal(null)}>
                Cancel
              </button>
              <button
                style={{ flex: 2, padding: '12px', backgroundColor: submittingQuote ? '#A8D5C2' : '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, cursor: submittingQuote ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 14 }}
                onClick={submitQuote}
                disabled={submittingQuote}
              >
                {submittingQuote ? 'Sending...' : '✅ Send Quote to Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FULL IMAGE LIGHTBOX */}
      {enlargedImage && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setEnlargedImage(null)}
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <button style={{ position: 'absolute', top: -44, right: 0, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#FFF', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 16, fontWeight: 700 }} onClick={() => setEnlargedImage(null)}>
              ✕ Close
            </button>
            <img
              src={enlargedImage}
              alt="Prescription full view"
              style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 10, boxShadow: '0 10px 60px rgba(0,0,0,0.5)' }}
            />
          </div>
        </div>
      )}

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontWeight: 700, margin: 0, fontSize: 18 }}>📋 Prescriptions</h2>
          <p style={{ color: '#888', fontSize: 13, margin: '4px 0 0' }}>
            Review customer prescriptions — approve before dispensing
          </p>
        </div>
        {pendingCount > 0 && (
          <span style={{ backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D', borderRadius: 20, padding: '4px 14px', fontSize: 13, fontWeight: 700 }}>
            ⏳ {pendingCount} pending review
          </span>
        )}
      </div>

      {/* FILTER TABS */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, backgroundColor: '#F1F3F5', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[
          { id: 'pending',  label: `⏳ Pending (${prescriptions.filter(p => p.status === 'pending').length})` },
          { id: 'quoted',   label: `💰 Quoted (${prescriptions.filter(p => p.status === 'quoted').length})` },
          { id: 'ordered',  label: `✅ Ordered (${prescriptions.filter(p => p.status === 'ordered').length})` },
          { id: 'rejected', label: `✕ Rejected (${prescriptions.filter(p => p.status === 'rejected').length})` },
          { id: 'all',      label: `📋 All (${prescriptions.length})` },
        ].map(tab => (
          <button key={tab.id}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: filter === tab.id ? 700 : 400, backgroundColor: filter === tab.id ? '#FFF' : 'transparent', color: filter === tab.id ? '#1D9E75' : '#888' }}
            onClick={() => setFilter(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* LIST */}
      {loading ? (
        <p style={{ color: '#888', padding: 20 }}>Loading prescriptions...</p>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, backgroundColor: '#FFF', borderRadius: 12, border: '1px solid #E5E7EB' }}>
          <p style={{ fontSize: 40, marginBottom: 12 }}>📋</p>
          <p style={{ fontWeight: 600, color: '#333', margin: '0 0 6px' }}>No prescriptions</p>
          <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
            {filter === 'pending' ? 'No prescriptions waiting for review' : `No ${filter} prescriptions yet`}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {filtered.map(p => {
            const confStyle = CONF_STYLE[p.aiConfidence] || CONF_STYLE.medium;
            const imageUrl = p.imageData || p.imageUrl || p.imageUri;
            const medicines = p.aiMedicines || p.analysisResult?.medicines || [];
            const hasAnalysis = medicines.length > 0 || p.aiPatientName || p.aiDoctorName || p.analysisResult;
            const meta = statusMeta(p.status);

            return (
              <div key={p.id} style={{ backgroundColor: '#FFF', borderRadius: 14, border: `1.5px solid ${meta.border}`, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>

                {/* CARD HEADER */}
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: meta.headerBg }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 21, backgroundColor: '#E8F8F2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>👤</div>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 15, margin: '0 0 2px' }}>{p.customerName || 'Customer'}</p>
                      <p style={{ fontSize: 12, color: '#888', margin: 0 }}>{p.customerEmail} · {new Date(p.createdAt).toLocaleString()}</p>
                    </div>
                  </div>
                  <span style={{ backgroundColor: meta.badgeBg, color: meta.badgeColor, padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 700 }}>
                    {meta.label}
                  </span>
                </div>

                {/* MAIN CONTENT — IMAGE + AI SIDE BY SIDE */}
                <div style={{ display: 'grid', gridTemplateColumns: imageUrl ? '1fr 1fr' : '1fr', gap: 0 }}>

                  {/* PRESCRIPTION IMAGE */}
                  {imageUrl && (
                    <div style={{ padding: 20, borderRight: '1px solid #F3F4F6', backgroundColor: '#FAFAFA' }}>
                      <p style={{ fontWeight: 700, fontSize: 13, color: '#444', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                        📄 Prescription Image
                        <span style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>· tap to zoom</span>
                      </p>
                      <div
                        style={{ cursor: 'zoom-in', borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB', backgroundColor: '#FFF' }}
                        onClick={() => setEnlargedImage(imageUrl)}
                      >
                        <img
                          src={imageUrl}
                          alt="Prescription"
                          style={{ width: '100%', maxHeight: 380, objectFit: 'contain', display: 'block' }}
                          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                        <div style={{ display: 'none', alignItems: 'center', justifyContent: 'center', height: 120, color: '#888', fontSize: 13, flexDirection: 'column', gap: 8 }}>
                          <span style={{ fontSize: 32 }}>🖼️</span>
                          <span>Image could not load</span>
                        </div>
                      </div>
                      <button
                        style={{ marginTop: 10, width: '100%', padding: '8px', backgroundColor: '#F1F3F5', color: '#555', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                        onClick={() => setEnlargedImage(imageUrl)}
                      >
                        🔍 View Full Size
                      </button>
                    </div>
                  )}

                  {/* AI ANALYSIS */}
                  <div style={{ padding: 20 }}>
                    <p style={{ fontWeight: 700, fontSize: 13, color: '#444', margin: '0 0 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>🤖 AI Analysis</span>
                      {p.aiConfidence && (
                        <span style={{ ...confStyle, padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                          {p.aiConfidence} confidence
                        </span>
                      )}
                    </p>

                    {!hasAnalysis ? (
                      <div style={{ backgroundColor: '#F7F8FA', borderRadius: 10, padding: 16, textAlign: 'center', color: '#888' }}>
                        <p style={{ fontSize: 24, margin: '0 0 8px' }}>🤖</p>
                        <p style={{ fontSize: 13, margin: 0 }}>No AI analysis available. Customer submitted without analysis.</p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

                        {/* PATIENT + DOCTOR */}
                        {(p.aiPatientName || p.aiDoctorName || p.aiDate) && (
                          <div style={{ backgroundColor: '#EFF6FF', borderRadius: 10, padding: 14, border: '1px solid #BFDBFE' }}>
                            <p style={{ fontWeight: 700, fontSize: 12, color: '#1D4ED8', margin: '0 0 8px' }}>📋 Prescription Details</p>
                            {p.aiPatientName && (
                              <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                                <span style={{ fontSize: 12, color: '#888', width: 60 }}>Patient</span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#1A1A1A' }}>{p.aiPatientName}</span>
                              </div>
                            )}
                            {p.aiDoctorName && (
                              <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                                <span style={{ fontSize: 12, color: '#888', width: 60 }}>Doctor</span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#1A1A1A' }}>{p.aiDoctorName}</span>
                              </div>
                            )}
                            {(p.aiDate || p.analysisResult?.date) && (
                              <div style={{ display: 'flex', gap: 8 }}>
                                <span style={{ fontSize: 12, color: '#888', width: 60 }}>Date</span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: '#1A1A1A' }}>{p.aiDate || p.analysisResult?.date}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* MEDICINES */}
                        {medicines.length > 0 && (
                          <div>
                            <p style={{ fontWeight: 700, fontSize: 12, color: '#1D9E75', margin: '0 0 8px' }}>
                              💊 Medicines Extracted ({medicines.length})
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {medicines.map((med, i) => (
                                <div key={i} style={{ backgroundColor: '#E8F8F2', borderRadius: 8, padding: '10px 14px', border: '1px solid #B2DFD0' }}>
                                  <p style={{ fontWeight: 700, fontSize: 13, color: '#1D6B52', margin: '0 0 4px' }}>{med.name}</p>
                                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                    {med.dosage && <span style={{ fontSize: 11, color: '#555' }}>💊 {med.dosage}</span>}
                                    {med.frequency && <span style={{ fontSize: 11, color: '#555' }}>🔄 {med.frequency}</span>}
                                    {med.quantity && <span style={{ fontSize: 11, color: '#555' }}>📦 Qty: {med.quantity}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* NOTES */}
                        {(p.aiNotes || p.analysisResult?.notes) && (
                          <div style={{ backgroundColor: '#FEF9C3', borderRadius: 8, padding: 12, border: '1px solid #FCD34D' }}>
                            <p style={{ fontSize: 12, color: '#92400E', margin: 0 }}>
                              📝 {p.aiNotes || p.analysisResult?.notes}
                            </p>
                          </div>
                        )}

                        {/* CUSTOMER NOTE */}
                        {p.customerNote && (
                          <div style={{ backgroundColor: '#F7F8FA', borderRadius: 8, padding: 12, border: '1px solid #E5E7EB' }}>
                            <p style={{ fontSize: 12, color: '#555', margin: 0 }}>
                              💬 <strong>Customer note:</strong> {p.customerNote}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* REJECTION REASON */}
                    {p.status === 'rejected' && p.rejectionReason && (
                      <div style={{ marginTop: 10, backgroundColor: '#FEE2E2', borderRadius: 8, padding: 12, border: '1px solid #FCA5A5' }}>
                        <p style={{ fontSize: 12, color: '#B91C1C', margin: 0 }}>
                          ✕ <strong>Rejection reason:</strong> {p.rejectionReason}
                        </p>
                      </div>
                    )}

                    {/* QUOTE SENT */}
                    {p.quote && (
                      <div style={{ marginTop: 10, backgroundColor: '#EFF6FF', borderRadius: 8, padding: 12, border: '1px solid #BFDBFE' }}>
                        <p style={{ fontWeight: 700, fontSize: 12, color: '#1D4ED8', margin: '0 0 8px' }}>
                          {p.status === 'ordered' ? '✅ Order placed from this quote' : '💰 Quote sent — awaiting customer payment'}
                        </p>
                        {p.quote.items.map((it, i) => (
                          <p key={i} style={{ fontSize: 12, color: '#1A1A1A', margin: '0 0 3px' }}>
                            {it.name} x{it.quantity} — GH₵ {(it.customerPrice * it.quantity).toFixed(2)}
                          </p>
                        ))}
                        <p style={{ fontSize: 12, color: '#555', margin: '6px 0 0' }}>Delivery: GH₵ {p.quote.deliveryFee.toFixed(2)}</p>
                        <p style={{ fontSize: 13, fontWeight: 700, color: '#1D4ED8', margin: '4px 0 0' }}>Total: GH₵ {p.quote.total.toFixed(2)}</p>
                      </div>
                    )}

                    {/* REVIEWED BY */}
                    {p.reviewedAt && (
                      <p style={{ fontSize: 11, color: '#888', margin: '10px 0 0' }}>
                        Reviewed {new Date(p.reviewedAt).toLocaleString()} by {p.reviewedBy}
                      </p>
                    )}
                    {p.quotedAt && (
                      <p style={{ fontSize: 11, color: '#888', margin: '4px 0 0' }}>
                        Quoted {new Date(p.quotedAt).toLocaleString()} by {p.quotedBy}
                      </p>
                    )}
                  </div>
                </div>

                {/* ACTION BUTTONS */}
                {p.status === 'pending' && (
                  <div style={{ padding: '14px 20px', borderTop: '1px solid #F3F4F6', display: 'flex', gap: 10, backgroundColor: '#FAFAFA' }}>
                    <button
                      style={{ flex: 1, padding: '11px', backgroundColor: '#1D9E75', color: '#FFF', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
                      onClick={() => openQuoteModal(p)}
                    >
                      💰 Approve & Send Quote
                    </button>
                    <button
                      style={{ flex: 1, padding: '11px', backgroundColor: '#FEE2E2', color: '#B91C1C', border: '1px solid #FCA5A5', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}
                      onClick={async () => {
                        const reason = window.prompt('Reason for rejection (optional):');
                        await updateStatus(p.id, 'rejected', reason || 'Prescription could not be verified');
                      }}
                    >
                      ✕ Reject
                    </button>
                  </div>
                )}

                {p.status === 'rejected' && (
                  <div style={{ padding: '12px 20px', borderTop: '1px solid #F3F4F6' }}>
                    <button
                      style={{ width: '100%', padding: '10px', backgroundColor: '#F7F8FA', color: '#555', border: '1px solid #E5E7EB', borderRadius: 10, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
                      onClick={() => updateStatus(p.id, 'pending', null)}
                    >
                      ↩ Reconsider — Move back to pending
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}